import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ChatPage from '@/pages/dashboard/library/ChatPage.jsx'
import * as API from '@/services/api.js'
import { handleError } from '@/services/errorHandler.js'
import { toast } from 'sonner'
import { MainLoader } from '@/components/loader/MainLoader.jsx'
import { useCreativeAiChat } from '@/context-state/CreativeAiChatContext.jsx'
import { AnalyticsService } from '@/services/analytics.js'

// Per-chat state cache so messages survive switching between chats
const chatStateCache = new Map()

const getOrCreateChatState = (chatId) => {
    if (!chatId) {
        return { messages: [], isFirstMessage: true, isLoading: false, isTyping: false, completedIds: null, instructionId: null }
    }
    if (!chatStateCache.has(chatId)) {
        chatStateCache.set(chatId, { messages: [], isFirstMessage: true, isLoading: false, isTyping: false, completedIds: null, instructionId: null })
    }
    return chatStateCache.get(chatId)
}

const updateChatStateCache = (chatId, updates) => {
    if (!chatId) {
        return
    }
    const current = getOrCreateChatState(chatId)
    chatStateCache.set(chatId, { ...current, ...updates })
}

// Broadcast loading state changes so the sidebar can show per-chat loading indicators
const chatLoadingListeners = new Set()

export const subscribeToChatLoading = (listener) => {
    chatLoadingListeners.add(listener)
    return () => chatLoadingListeners.delete(listener)
}

const broadcastLoadingState = (chatId, isLoading) => {
    chatLoadingListeners.forEach(listener => listener(chatId, isLoading))
}

export default function ChatHistoryPage() {

    const { chatId } = useParams()
    const navigate = useNavigate()
    const { setSelectedInstructionId } = useCreativeAiChat()
    const [loadState, setLoadState] = useState({ status: 'idle' })

    // State for the currently viewed chat
    const [messages, setMessages] = useState([])
    const [activeChatId, setActiveChatId] = useState(null)
    const [isFirstMessage, setIsFirstMessage] = useState(true)
    const [isLoading, setIsLoading] = useState(false)
    const [isTyping, setIsTyping] = useState(false)
    const [preCompletedIds, setPreCompletedIds] = useState(null)

    const justCreatedRef = useRef(null)
    const activeChatIdRef = useRef(null)
    // Stable key that only changes when we explicitly want to remount ChatPage (e.g. loading a different chat)
    const [chatPageKey, setChatPageKey] = useState('new-chat')

    useEffect(() => {
        activeChatIdRef.current = activeChatId
    }, [activeChatId])

    // Create chatId-scoped setters that route updates to the correct chat.
    // If the ChatPage that calls these has been switched away from, updates go to cache only.
    const scopedSetters = useMemo(() => {
        const targetChatId = activeChatId

        const scopedSetMessages = (updater) => {
            if (!targetChatId) {
                // New chat (no chatId yet) — use React state directly
                setMessages(updater)
                return
            }
            // Existing chat — update cache and conditionally update React state
            const cached = getOrCreateChatState(targetChatId)
            const next = typeof updater === 'function' ? updater(cached.messages) : updater
            updateChatStateCache(targetChatId, { messages: next })

            if (activeChatIdRef.current === targetChatId) {
                setMessages(next)
            }
        }

        const scopedSetIsLoading = (value) => {
            if (targetChatId) {
                updateChatStateCache(targetChatId, { isLoading: value })
                broadcastLoadingState(targetChatId, value)
            }
            if (!targetChatId || activeChatIdRef.current === targetChatId) {
                setIsLoading(value)
            }
        }

        const scopedSetIsTyping = (value) => {
            if (targetChatId) {
                updateChatStateCache(targetChatId, { isTyping: value })
            }
            if (!targetChatId || activeChatIdRef.current === targetChatId) {
                setIsTyping(value)
            }
        }

        const scopedSetIsFirstMessage = (value) => {
            if (targetChatId) {
                updateChatStateCache(targetChatId, { isFirstMessage: value })
            }
            if (!targetChatId || activeChatIdRef.current === targetChatId) {
                setIsFirstMessage(value)
            }
        }

        return {
            setMessages: scopedSetMessages,
            setIsLoading: scopedSetIsLoading,
            setIsTyping: scopedSetIsTyping,
            setIsFirstMessage: scopedSetIsFirstMessage
        }
    }, [activeChatId])

    useEffect(() => {
        AnalyticsService.page('chat-history-page')
    }, [])

    useEffect(() => {
        if (!chatId) {
            resetToFreshChat()
            return
        }

        if (chatId === justCreatedRef.current && messages.length > 0) {
            setLoadState({ status: 'loaded', chatId })
            return
        }

        justCreatedRef.current = null

        // Restore from cache if available
        const cached = chatStateCache.get(chatId)
        if (cached && cached.messages.length > 0) {
            restoreFromCache(chatId, cached)
        } else if (loadState.chatId !== chatId || loadState.status === 'error') {
            loadExistingChat(chatId)
        }
    }, [chatId])

    const restoreFromCache = (targetChatId, cached) => {
        setMessages(cached.messages)
        setActiveChatId(targetChatId)
        setIsFirstMessage(cached.isFirstMessage)
        setIsLoading(cached.isLoading)
        setIsTyping(cached.isTyping)
        setPreCompletedIds(cached.completedIds)
        setSelectedInstructionId(cached.instructionId || null)
        setLoadState({ status: 'loaded', chatId: targetChatId })
        setChatPageKey(targetChatId)
    }

    const resetToFreshChat = () => {
        setMessages([])
        setActiveChatId(null)
        setIsFirstMessage(true)
        setIsLoading(false)
        setIsTyping(false)
        setLoadState({ status: 'idle' })
        setPreCompletedIds(null)
        justCreatedRef.current = null
        setSelectedInstructionId(null)
        setChatPageKey('new-chat-' + Date.now())
    }

    const loadExistingChat = async (targetChatId) => {
        try {
            setLoadState({ status: 'loading', chatId: targetChatId })

            const [messagesResponse, loadResponse] = await Promise.all([
                API.getChatMessages(targetChatId),
                API.loadChat(targetChatId)
            ])

            const chatMessages = messagesResponse.data || []

            const formattedMessages = chatMessages.map((msg, index) => {
                const formatted = {
                    id: index + 1,
                    role: msg.role,
                    content: msg.content,
                    uiPayload: msg.uiPayload || null,
                    timestamp: new Date(msg.createdAt)
                }
                if (msg.attachments) {
                    formatted.attachments = msg.attachments.map(a => ({
                        type: 'file',
                        url: a.url,
                        filename: a.filename || 'attachment',
                        mediaType: a.mediaType
                    }))
                } else if (msg.fileData) {
                    const files = Array.isArray(msg.fileData) ? msg.fileData : [msg.fileData]
                    formatted.attachments = files.map(f => ({
                        type: 'file',
                        url: f.fileUri,
                        filename: 'attachment',
                        mediaType: f.mimeType
                    }))
                }
                return formatted
            })

            const completedIds = formattedMessages
                .filter(msg => msg.role === 'assistant')
                .map(msg => msg.id)

            const chatInstructionId = loadResponse.data?.instructionId || null

            updateChatStateCache(targetChatId, {
                messages: formattedMessages,
                isFirstMessage: false,
                isLoading: false,
                isTyping: false,
                completedIds,
                instructionId: chatInstructionId
            })

            setMessages(formattedMessages)
            setActiveChatId(targetChatId)
            setIsFirstMessage(false)
            setPreCompletedIds(completedIds)
            setSelectedInstructionId(chatInstructionId)
            setLoadState({ status: 'loaded', chatId: targetChatId })
            setChatPageKey(targetChatId)
            AnalyticsService.track('chat-history-loaded', { chatId: targetChatId, messageCount: formattedMessages.length })

        } catch (e) {
            handleError(e)
            const errorMessage = e.response?.data?.message || 'Failed to load chat history'
            toast.error(errorMessage)
            setLoadState({ status: 'error', chatId: targetChatId })
        }
    }

    const handleChatCreated = useCallback((newChatId) => {
        justCreatedRef.current = newChatId
        setActiveChatId(newChatId)
        setIsFirstMessage(false)
        setLoadState({ status: 'loaded', chatId: newChatId })
        // Don't change chatPageKey — keep the same ChatPage instance alive
        navigate(newChatId, { replace: true })
        AnalyticsService.track('chat-created-from-history-page', { chatId: newChatId })
    }, [navigate])

    const handleActiveChatDeleted = useCallback(() => {
        AnalyticsService.track('chat-active-chat-deleted')
        if (activeChatIdRef.current) {
            chatStateCache.delete(activeChatIdRef.current)
        }
        navigate('/chat', { replace: true })
    }, [navigate])

    // Show loader while loading from API
    if (chatId && (loadState.status === 'loading' || loadState.status === 'idle') && chatId !== justCreatedRef.current) {
        return <MainLoader />
    }

    return (
        <ChatPage
            key={chatPageKey}
            externalChatId={activeChatId}
            externalMessages={messages}
            externalSetMessages={scopedSetters.setMessages}
            externalIsFirstMessage={isFirstMessage}
            externalSetIsFirstMessage={scopedSetters.setIsFirstMessage}
            externalSetChatId={setActiveChatId}
            externalIsLoading={isLoading}
            externalSetIsLoading={scopedSetters.setIsLoading}
            externalIsTyping={isTyping}
            externalSetIsTyping={scopedSetters.setIsTyping}
            initialCompletedMessageIds={preCompletedIds}
            chatSource="chat-page"
            onChatCreated={handleChatCreated}
            onActiveChatDeleted={handleActiveChatDeleted}
        />
    )
}
